import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/dash/Common";
import { PaymentBadge } from "@/components/Badges";
import { FullLoader } from "@/components/ProtectedRoute";
import { useApi } from "@/hooks/useApi";
import { api, errMsg, openFile } from "@/lib/api";
import { fmtDateTime, rupiah, PAYMENT_STATUS } from "@/lib/format";

function VerifyDialog({ payment, status, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/payments/${payment.id}`, { status, note });
      toast.success(status === "disetujui" ? "Pembayaran disetujui, paket siswa aktif" : "Pembayaran ditolak");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="verify-payment-dialog">
        <DialogHeader><DialogTitle>{status === "disetujui" ? "Setujui" : "Tolak"} pembayaran {payment.invoice}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{payment.user_name} · {payment.package_name} · {rupiah(payment.amount)}</p>
        <Textarea placeholder={status === "ditolak" ? "Alasan penolakan (tampil ke siswa)" : "Catatan (opsional)"} value={note} onChange={(e) => setNote(e.target.value)} data-testid="verify-note-input" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={busy} className={status === "disetujui" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"} data-testid="verify-confirm-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Konfirmasi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PaymentsPage() {
  const { data, loading, reload } = useApi("/admin/payments");
  const [filter, setFilter] = useState("menunggu_verifikasi");
  const [action, setAction] = useState(null);
  const rows = useMemo(() => (data || []).filter((p) => !filter || p.status === filter), [data, filter]);
  if (loading && !data) return <FullLoader />;
  return (
    <div data-testid="admin-payments-page">
      <PageHeader eyebrow="Pembayaran" title="Verifikasi pembayaran" description="Periksa bukti transfer. Menyetujui pembayaran akan mengaktifkan paket dan masa berlaku siswa secara otomatis." />
      <div className="flex flex-wrap gap-1 mb-4">
        {[["", "Semua"], ...Object.entries(PAYMENT_STATUS).map(([k, v]) => [k, v.label])].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${filter === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid={`payments-filter-${k || "all"}`}>
            {l} ({(data || []).filter((p) => !k || p.status === k).length})
          </button>
        ))}
      </div>
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>Siswa</TableHead><TableHead>Paket</TableHead><TableHead>Nominal</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-sm text-slate-500">Tidak ada data</TableCell></TableRow>}
            {rows.map((p) => (
              <TableRow key={p.id} data-testid={`payment-row-${p.invoice}`}>
                <TableCell><p className="font-mono text-sm font-semibold">{p.invoice}</p><p className="text-xs text-slate-500">{fmtDateTime(p.created_at)}</p></TableCell>
                <TableCell><p className="font-medium">{p.user_name}</p><p className="text-xs text-slate-500">{p.user_email}</p>{p.sender_name && <p className="text-xs text-slate-500">Pengirim: {p.sender_name} ({p.sender_bank})</p>}</TableCell>
                <TableCell>{p.package_name}</TableCell>
                <TableCell className="font-semibold">{rupiah(p.amount)}</TableCell>
                <TableCell><PaymentBadge status={p.status} />{p.note && <p className="text-xs text-slate-500 mt-1 max-w-40">{p.note}</p>}</TableCell>
                <TableCell className="text-right whitespace-nowrap space-x-1">
                  {p.proof_file_id && <Button size="sm" variant="ghost" onClick={() => openFile(p.proof_file_id).catch((e) => toast.error(errMsg(e)))} data-testid={`view-proof-${p.invoice}`}><Eye className="h-4 w-4 mr-1" />Bukti</Button>}
                  {p.status !== "disetujui" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setAction({ p, status: "disetujui" })} data-testid={`approve-payment-${p.invoice}`}>Setujui</Button>}
                  {["menunggu_verifikasi", "menunggu_pembayaran"].includes(p.status) && <Button size="sm" variant="outline" className="text-red-600" onClick={() => setAction({ p, status: "ditolak" })} data-testid={`reject-payment-${p.invoice}`}>Tolak</Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {action && <VerifyDialog payment={action.p} status={action.status} onClose={() => setAction(null)} onDone={() => { setAction(null); reload(); }} />}
    </div>
  );
}
